import { useState } from 'react';
import { Copy, ArrowDown, Share2 } from './icons';
import { copyResult, trackToolAction } from '../utils/analytics';
import { contrastReportHtml, contrastReportMarkdown, type ContrastReport } from '../utils/contrastReport';

export default function ContrastReportActions({ report, tool = 'contrast' }: { report: ContrastReport; tool?: string }) {
  const [message, setMessage] = useState('');
  const [fallback, setFallback] = useState('');

  async function copy(format: 'markdown' | 'url') {
    const payload = format === 'markdown' ? contrastReportMarkdown(report) : report.sourceUrl;
    if (await copyResult(payload, tool, format === 'url' ? 'share' : 'copy_report', format)) {
      setFallback('');
      setMessage(format === 'markdown' ? 'Markdown report copied.' : 'Reproducible result link copied.');
    } else {
      setFallback(payload);
      setMessage('Clipboard access is unavailable. Select and copy the report below.');
    }
  }

  function download(format: 'json' | 'html') {
    const payload = format === 'json' ? JSON.stringify(report, null, 2) + '\n' : contrastReportHtml(report);
    const url = URL.createObjectURL(new Blob([payload], { type: format === 'json' ? 'application/json;charset=utf-8' : 'text/html;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'color-match-contrast-report.' + format;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    trackToolAction(tool, 'export_report', format);
    setMessage(format.toUpperCase() + ' report download started.');
  }

  return <div className="mt-5 space-y-3">
    <p className="text-sm text-ink-secondary">Save the tested roles, original and adjusted colors, raw pass/fail results, calculation versions and a link that restores these inputs.</p>
    <div className="flex flex-wrap gap-3">
      <button onClick={() => void copy('markdown')} className="ui-button-secondary"><Copy size={18} aria-hidden="true" />Copy Markdown report</button>
      <button onClick={() => download('json')} className="ui-button-secondary"><ArrowDown size={18} aria-hidden="true" />Download JSON report</button>
      <button onClick={() => download('html')} className="ui-button-secondary"><ArrowDown size={18} aria-hidden="true" />Download HTML report</button>
      <button onClick={() => void copy('url')} className="ui-button-secondary"><Share2 size={18} aria-hidden="true" />Copy report link</button>
    </div>
    <p role="status" aria-live="polite" className="text-sm text-ink-secondary">{message}</p>
    {fallback && <textarea aria-label="Report to copy manually" readOnly value={fallback} onFocus={event => event.target.select()} className="ui-input block w-full min-h-40 font-mono text-sm" />}
  </div>;
}
